import axios from 'axios';
import createAuthRefreshInterceptor, { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';
import { Platform } from 'react-native';

import config from '~/config';

import { LoginResponse, LoginResponseReturnData } from './AuthDataService/types';

export const UNAUTHORIZED_STATUS_CODES = [401, 403];

export const SESSION_EXPIRED = 'INVALID_SESSION';

const apiClient = axios.create({
    baseURL: `${config.API_BASE_URL}`,
    headers: {
        'Content-type': 'application/json',
        Accept: 'application/json',
    },
});

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        // TODO: log for test env only
        console.log('API error:', error.message);
        return Promise.reject(error);
    }
);

let authInterceptorIndex: number;
let unauthorizedInterceptorIndex: number;
let sessionInvalidHandler: (() => void) | undefined;

/**
 * Runs the armed session-invalid handler, at most once. Expiry can be observed from several places at
 * the same time (any of the interceptors below, or a caller reading errorCode off a response body), so
 * every one of them reports it here rather than logging out on its own.
 */
export const notifySessionInvalid = () => {
    if (!sessionInvalidHandler) {
        return;
    }
    const handler = sessionInvalidHandler;
    sessionInvalidHandler = undefined;
    handler();
};

apiClient.interceptors.response.use((response) => {
    if ((response?.config as AxiosAuthRefreshRequestConfig)?.skipAuthRefresh) {
        return response;
    }
    if ((response?.data as any)?.errorCode === SESSION_EXPIRED) {
        notifySessionInvalid();
    }
    return response;
});

export const ejectSessionRefetchInterceptor = () => {
    sessionInvalidHandler = undefined;
    if (authInterceptorIndex !== undefined) {
        apiClient.interceptors.response.eject(authInterceptorIndex);
        authInterceptorIndex = undefined;
    }
};

export const ejectUnauthorizedLogoutInterceptor = () => {
    sessionInvalidHandler = undefined;
    if (unauthorizedInterceptorIndex !== undefined) {
        apiClient.interceptors.response.eject(unauthorizedInterceptorIndex);
        unauthorizedInterceptorIndex = undefined;
    }
};

export const setupSessionRefetchInterceptor = async (
    token: string,
    onComplete: (authData: LoginResponseReturnData) => void,
    onError: () => void
) => {
    ejectSessionRefetchInterceptor();
    ejectUnauthorizedLogoutInterceptor();

    sessionInvalidHandler = onError;

    authInterceptorIndex = createAuthRefreshInterceptor(
        apiClient,
        async (failedRequest) => {
            try {
                const response = await apiClient.post<LoginResponse>(
                    '/api/24yiedlogin/loginWithToken',
                    {
                        token,
                        apiVersion: config.API_VERSION,
                        website: config.WEBSITE,
                        source: Platform.OS.toUpperCase(),
                        sourceVersion: config.SOURCE_VERSION,
                        sourceType: Platform.OS.toUpperCase(),
                    },
                    { skipAuthRefresh: true } as AxiosAuthRefreshRequestConfig
                );

                if (response.status !== 200 || !response.data.returnData) {
                    notifySessionInvalid();
                    return Promise.reject('Failed to refetch session.');
                }

                const authData = response.data.returnData;
                authData.token = token;

                setupAuthHeaders(authData.sessionId, authData.language);
                failedRequest.response.config.headers.s = authData.sessionId;
                onComplete(authData);
            } catch (e) {
                notifySessionInvalid();
                return Promise.reject('Failed to refetch session.');
            }
            return Promise.resolve();
        },
        { statusCodes: UNAUTHORIZED_STATUS_CODES }
    );
};

export const setupUnauthorizedLogoutInterceptor = (onUnauthorized: () => void) => {
    ejectSessionRefetchInterceptor();
    ejectUnauthorizedLogoutInterceptor();

    unauthorizedInterceptorIndex = apiClient.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error?.config?.skipAuthRefresh) {
                return Promise.reject(error);
            }
            // Axios builds the interceptor chain per request at dispatch time, so ejecting below does
            // not detach this handler from requests that are already in flight; notifySessionInvalid is
            // what keeps a burst of parallel 401s down to a single logout.
            if (UNAUTHORIZED_STATUS_CODES.includes(error?.response?.status)) {
                const handler = sessionInvalidHandler;
                ejectUnauthorizedLogoutInterceptor();
                handler?.();
            }
            return Promise.reject(error);
        }
    );

    sessionInvalidHandler = onUnauthorized;
};

export const setupAuthHeaders = (sessionId: string, language: string) => {
    apiClient.defaults.headers.common.s = sessionId;
    apiClient.defaults.headers.common.l = language;
};

export const clearAuthHeaders = () => {
    delete apiClient.defaults.headers.common.s;
    delete apiClient.defaults.headers.common.l;
};

export default apiClient;
